import { Env } from "@stencil/core"

let isAI12ZControlEnabled = null

export function getEndpoint(env: string) {
  env = env || Env.DATA_MODE

  switch (env) {
    case "local":
      return `http://127.0.0.1:5000`
    case "dev":
      return `https://dev-api.ai12z.net`
    default:
      return `https://api.ai12z.net`
  }
}

export function getMetaInfo() {
  const meta = {
    origin: window?.location?.origin,
    title: document?.title,
    userAgent: navigator?.userAgent,
    referrer: document?.referrer,
    language: navigator?.language,
    description: document.querySelector('meta[name="description"]')?.getAttribute("content"),
  }
  return meta
}

async function apiFetch(url: string, payload: any) {
  try {
    const meta = {
      origin: window?.location?.origin,
      title: document?.title,
      userAgent: navigator?.userAgent,
      referrer: document?.referrer,
      language: navigator?.language,
      description: document.querySelector('meta[name="description"]')?.getAttribute("content"),
    }
    if (!payload.apiKey) {
      payload.apiKey = Env.DATA_KEY
    }
    if (!payload.apiKey) throw new Error(`Public Key is required. Please provide using attribute "data-key".`)

    payload = { ...payload, meta }
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const jsonResponse = await response.json()

    return await jsonResponse
  } catch (error) {
    throw error
  }
}

export function format(first: string, middle: string, last: string): string {
  return (first || "") + (middle ? ` ${middle}` : "") + (last ? ` ${last}` : "")
}

export async function search(apiKey: string, env: string, terms: string) {
  if (!terms) return []
  const endpoint = `${getEndpoint(env)}/bot/search`
  const data = {
    apiKey,
    query: terms,
    num_docs: 4,
  }
  return await apiFetch(endpoint, data)
}

export async function askAI(apiKey: string, env: string, question: string, conversationId: string, followUp: boolean = false, metadata: any = null) {
  const endpoint = `${getEndpoint(env)}/bot/askai`
  const data = {
    apiKey,
    query: question,
    conversation_id: followUp ? conversationId : "",
    format: "html",
    attributes: metadata,
  }

  return await apiFetch(endpoint, data)
}

export async function getAudioContent(apiKey: string, env: string, content: string, voiceType: string) {
  const endpoint = `${getEndpoint(env)}/bot/synthesize`
  const data = {
    text: content,
    apiKey: apiKey,
    voiceType: voiceType,
  }
  return await apiFetch(endpoint, data)
}

export async function askImgAI(apiKey: string, env: string, base64Images: any, question: string) {
  const endpoint = `${getEndpoint(env)}/bot/askai`
  const data = {
    query: question,
    base64Images: base64Images,
    apiKey: apiKey,
    format: "html",
  }
  return await apiFetch(endpoint, data)
}

export async function sendFeedback(apiKey: string, env: string, insightId: string, feedback: 1 | -1) {
  const endpoint = `${getEndpoint(env)}/bot/feedback`
  const data = {
    apiKey,
    insight_id: insightId,
    feedback,
  }

  return await apiFetch(endpoint, data)
}

export function debounce(func, timeout = 300) {
  let timer
  return (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, timeout)
  }
}

export async function checkStatus(apiKey: string, env: string) {
  if (isAI12ZControlEnabled === null) {
    try {
      const endpoint = `${getEndpoint(env)}/bot/status`
      const res = await apiFetch(endpoint, { apiKey })
      if (res) {
        isAI12ZControlEnabled = res.enable
      }
    } catch (error) {
      isAI12ZControlEnabled = false
    }
  }

  return isAI12ZControlEnabled
}

export function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function updateButtonEvents(htmlContent: string) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlContent, "text/html")
  const buttons = Array.from(doc.querySelectorAll("button"))

  let customButtons = buttons.map((button, index) => {
    const id = button.id || `button-${index}`
    const label = button.textContent || `Button ${index + 1}`
    button.id = id
    const originalClickHandler = button.getAttribute("onclick")
    button.removeAttribute("onclick")
    button.setAttribute("data-original-click", originalClickHandler)
    return {
      id,
      label,
      // originalClickHandler,
      // onClick: originalOnClick,
      onClick: () => alert(`Button ${id} clicked!`),
    }
  })
  // console.log("customButtons", customButtons)
  // Update the htmlContent with the modified buttons
  return { body: doc.body.innerHTML, buttons: customButtons }
}

export function getConfig(payload: any, env: string) {
  const { configId, apiKey, configType } = payload
  const endpoint = `${getEndpoint(env)}/bot/config`
  if (!payload.apiKey) {
    payload.apiKey = Env.DATA_KEY
  }
  const data = {
    configId,
    apiKey,
    configType,
  }
  return apiFetch(endpoint, data)
}

export function verifyRecaptcha(payload, env: string) {
  const endpoint = `${getEndpoint(env)}/bot/verify`
  if (!payload.apiKey) {
    payload.apiKey = Env.DATA_KEY
  }

  return apiFetch(endpoint, payload)
}

export function getOrCreateVisitorId(): string {
  const visitorIdKey = "ai12z-visitorId"
  let visitorId = localStorage.getItem(visitorIdKey)

  if (!visitorId) {
    visitorId = generateVisitorId()
    localStorage.setItem(visitorIdKey, visitorId)
  }

  return "ai12z-" + visitorId
}

function generateVisitorId(): string {
  return Date.now() + "-" + Math.random().toString(36).substr(2, 9)
}

export function getComponentByControlType(data: any, inputType: string) {
  const controlType = data.controlData?.data.source.carousel?.type
  const listElement = document.createElement("item-list")
  // listElement.items = data.controlData?.data.source.items
  // listElement.itemsPerPage = data.controlData?.data.source?.carousel?.itemPerPage
  // listElement.emptyMessage = data.controlData?.data.source?.carousel?.emptyMessage
  // listElement.formSettings = data.controlData?.data.source?.formSettings
  // listElement.buttonText = data.controlData?.data.source?.carousel?.buttonText
  listElement.carouselType = controlType
  listElement.inputType = inputType
  listElement.customTemplate = data.controlData.template
  listElement.customSource = data.controlData?.data.source
  listElement.isExpanded = data.isExpanded || false
  return listElement
}

export function getBotTemplate(type) {
  switch (type) {
    case "list":
      const listTemplate = [
        {
          name: "list",
          content: `{{#each items as |item index|}}
  <div class="carousel-card flex flex-col items-start mb-4 bg-white border h-auto border-gray-200 rounded-lg shadow md:flex-row md:max-w-xl  dark:border-gray-700" data-index="{{@index}}">
    <div class="relative carousel-container card-image {{#if (gte item.images.length  1)}}w-full{{else}}w-1/2{{/if}}" data-carousel-id="carousel-{{index}}" id="carousel-{{index}}" data-carousel="slide">
          <div class="relative h-64 overflow-hidden">
          {{#each item.images as |image imageIndex|}}      
            <img src="{{this}}" alt="Image for {{../title}}" class="card-image object-cover w-full h-96 md:h-auto md:mt-6 md:ml-2 hidden duration-700 ease-in-out"  data-carousel-item id="carousel-{{index}}-item-{{imageIndex}}"/>    
          {{/each}}
          </div>  
        {{#if (lte item.images.length 3)}} 
            {{#if (gte item.images.length 1)}}
              <div class="absolute z-30 flex -translate-x-1/2 bottom-5 left-1/2 space-x-3 rtl:space-x-reverse">
                {{#each images as |image imageIndex|}}
                  <button type="button" class="w-3 h-3 rounded-full" aria-current="true" aria-label="Slide {{@index}}" data-carousel-slide-to="{{index}}"  id="carousel-{{index}}-indicator-{{imageIndex}}"></button>
                {{/each}}
              </div>
            {{/if}}
          {{else}}
           <div class="absolute z-30 flex -translate-x-1/2 bottom-5 left-1/2 space-x-3 rtl:space-x-reverse">
                <button type="button" class="w-3 h-3 rounded-full bg-blue-600" aria-current="true" aria-label="Slide {{@index}}" data-carousel-prev  id="carousel-{{index}}-prev"></button>
                <button type="button" class="w-3 h-3 rounded-full bg-blue-600" aria-current="true" aria-label="Slide {{@index}}" data-carousel-next  id="carousel-{{index}}-next"></button>
            </div>
        {{/if}}
  </div>
      
    <div class="flex flex-col justify-between p-4 leading-normal">
        <h5 class="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white"> {{{this.title}}}</h5>
        {{#each subTitle}}       
          <p class="font-title text-md md:text-xl">{{this}}</p>      
        {{/each}}
        <p class="short-description mb-3 font-normal text-gray-700 dark:text-gray-400">{{{this.shortDescription}}}</p>
        <div class="long-description mb-3 font-normal text-gray-700 dark:text-gray-400 hidden">{{{this.description}}}</div>
        {{#if this.description}}
          <button  class="read-more text-blue-500 hover:text-blue-700 underline focus:outline-none"  data-index="{{@index}}"> Read More </button>
        {{/if}}
        <div class="flex flex-col items-center my-4">
            {{#each buttons}}      
              <button class="card-button list-btn uppercase pt-2 pb-1 px-4 flex-grow hover:bg-gradient-to-t md:flex-none" data-id="{{@index}}" data-url="{{this.url}}" data-type="{{this.type}}">
               {{this.label}}</button>
            {{/each}}
            {{#and ../buttonText formSettings}}
              <button class="card-button list-btn uppercase" data-type="panel">{{../buttonText}}</button>
            {{/and}}
        </div>
    </div>
</div>
{{/each}}
<div class="pagination-controls">
<button class="prev-btn list-btn" {{#unless hasPrev}}disabled{{/unless}}>Prev</button>
<span> Page {{getcurrentPage}} of {{getPageCount}} </span>
<button class="next-btn list-btn" {{#unless hasNext}}disabled{{/unless}}>Next</button>
</div>`,
        },
        {
          name: "detail",
          content: `<div class="flex flex-col items-center justify-center w-full h-full p-4">
          <div class="w-full md:col-span-2 h-fit">
            <dynamic-form form="{{json form}}" formSettings="{{json formSettings}}" theme="{{json theme}}"/>
          </div>   
          <div>    
            {{#each buttons}}    
              <button class="panel-button list-btn text-white" data-id="{{this.index}}" data-type="{{this.type}}">{{this.label}}</button>     
            {{/each}}
          </div>
          </div>`,
        },
      ]
      return listTemplate
  }
}

export function getTemplate(type) {
  switch (type) {
    case "list":
      const tempalteArray = [
        {
          name: "list",
          content: `{{#each items as |item index|}}
  <div class="carousel-card flex flex-col items-start mb-4 bg-white border h-auto border-gray-200 rounded-lg shadow md:flex-row  dark:border-gray-700" data-index="{{index}}">
  
  <div class="relative  md:w-1/3 carousel-container card-image {{#if (gte item.images.length  1)}}w-full{{else}}w-full md:w-1/2{{/if}}" data-carousel-id="carousel-{{index}}" id="carousel-{{index}}" data-carousel="slide">
          <div class="relative h-64 overflow-hidden">
          {{#each item.images as |image imageIndex|}}      
            <img src="{{this}}" alt="Image for {{../title}}" class="card-image object-cover w-full h-96 md:h-auto md:mt-6 md:ml-2 hidden duration-700 ease-in-out"  data-carousel-item id="carousel-{{index}}-item-{{imageIndex}}"/>    
          {{/each}}
          </div>  
        {{#if (lte item.images.length 3)}} 
            {{#if (gte item.images.length 1)}}
              <div class="absolute z-30 flex -translate-x-1/2 bottom-5 left-1/2 space-x-3 rtl:space-x-reverse">
                {{#each images as |image imageIndex|}}
                  <button type="button" class="w-3 h-3 rounded-full" aria-current="true" aria-label="Slide {{@index}}" data-carousel-slide-to="{{index}}"  id="carousel-{{index}}-indicator-{{imageIndex}}"></button>
                {{/each}}
              </div>
            {{/if}}
          {{else}}
           <div class="absolute z-30 flex -translate-x-1/2 bottom-5 left-1/2 space-x-3 rtl:space-x-reverse">
                <button type="button" class="w-3 h-3 rounded-full bg-blue-600" aria-current="true" aria-label="Slide {{@index}}" data-carousel-prev  id="carousel-{{index}}-prev"></button>
                <button type="button" class="w-3 h-3 rounded-full bg-blue-600" aria-current="true" aria-label="Slide {{@index}}" data-carousel-next  id="carousel-{{index}}-next"></button>
            </div>
        {{/if}}
  </div>
   
  
   
    <div class="flex flex-col justify-between p-4 leading-normal md:w-2/3 card-right">
        <h5 class="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white"> {{{this.title}}}</h5>
        {{#each subTitle}}       
          <p class="font-title text-md md:text-xl">{{this}}</p>      
        {{/each}}
        <p class="short-description mb-3 font-normal text-gray-700 dark:text-gray-400">{{{this.shortDescription}}}</p>
        <div class="long-description mb-3 font-normal text-gray-700 dark:text-gray-400 hidden">{{{this.description}}}</div>
        {{#if this.description}}
          <a  class="read-more text-blue-500 hover:text-blue-700 underline focus:outline-none"  data-index="{{@index}}"> Read More </a>
        {{/if}}
        <div class="flex items-center my-4">
            {{#each buttons as |button buttonIndex|}}      
              <button class="card-button list-btn uppercase pt-2 pb-1 px-4 flex-grow hover:bg-gradient-to-t md:flex-none" data-id="{{buttonIndex}}" data-url="{{this.url}}" data-type="{{this.type}}">
               {{this.label}}</button>
            {{/each}}
           {{#and ../buttonText formSettings}}
              <button class="card-button list-btn uppercase" data-type="panel">{{../buttonText}}</button>
            {{/and}}
        </div>
    </div>
</div>
{{/each}}
<div class="pagination-controls">
<button class="prev-btn list-btn" {{#unless hasPrev}}disabled{{/unless}}>Prev</button>
<span> Page {{getcurrentPage}} of {{getPageCount}} </span>
<button class="next-btn list-btn" {{#unless hasNext}}disabled{{/unless}}>Next</button>
</div>
`,
        },
        {
          name: "detail",
          content: `
          <div class="flex flex-col items-center justify-center w-full h-full p-4">
          <div class="w-full md:col-span-2 h-fit">
            <dynamic-form form="{{json form}}" formSettings="{{json formSettings}}" theme="{{theme}}"/>
          </div>   
          <div>    
            {{#each buttons}}    
              <button class="panel-button list-btn text-white" data-id="{{this.index}}" data-type="{{this.type}}">{{this.label}}</button>     
            {{/each}}
          </div>
          </div>`,
        },
      ]
      return tempalteArray
    case "slider":
      const sliderArray = [
        {
          name: "carousel",
          content: `
<div id="carouselExample" class="relative w-full" data-carousel="static">
  
            <div class="carousel-box relative bg-white overflow-hidden overflow-y-scroll h-56 rounded-lg md:h-96" style={ overflowY: "scroll", height: "30rem" }>
              {{#each items as |item index|}}
                  <div class="carousel-card max-w-sm hidden duration-700 ease-in-out md:max-w-xl" data-carousel-item id="carousel-item-{{index}}" data-index="{{index}}">
                     <div class="card-image relative w-full carousel-container" data-carousel-id="carousel-{{index}}" id="carousel-{{index}}" data-carousel="slide">
                        <div class="relative h-64 overflow-hidden rounded-lg">
                          {{#each item.images as |image imageIndex|}} 
                    
                              <img src="{{this}}" alt="Image for {{../title}}" class="card-image object-cover h-96  hidden duration-700 ease-in-out"  data-carousel-item id="carousel-{{index}}-item-{{imageIndex}}"/>    
                            
                          {{/each}}
                        </div> 
                    {{#if (lte item.images.length 3)}} 
                        <div class="absolute z-30 flex -translate-x-1/2 bottom-5 left-1/2 space-x-3 rtl:space-x-reverse">
                          {{#each images as |image imageIndex|}}
                            <button type="button" class="w-3 h-3 rounded-full" aria-current="true" aria-label="Slide {{@index}}" data-carousel-slide-to="{{index}}"  id="carousel-{{index}}-indicator-{{imageIndex}}"></button>
                          {{/each}}
                        </div>
                        {{else}}
                          <div class="absolute z-30 flex -translate-x-1/2 bottom-5 left-1/2 space-x-3 rtl:space-x-reverse">
                              <button type="button" class="w-3 h-3 rounded-full bg-blue-600" aria-current="true" aria-label="Slide {{@index}}" data-carousel-prev  id="carousel-{{index}}-prev"></button>
                              <button type="button" class="w-3 h-3 rounded-full bg-blue-600" aria-current="true" aria-label="Slide {{@index}}" data-carousel-next  id="carousel-{{index}}-next"></button>
                          </div>
                      {{/if}}
                    </div>  
                 
                  <div class="p-5">
                      <a href="#">
                          <h5 class="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{{{title}}}</h5>
                      </a>
                      <p class="short-description mb-3 font-normal text-gray-700 dark:text-gray-400">{{{shortDescription}}}</p>
                      <div class="long-description mb-3 font-normal text-gray-700 dark:text-gray-400 hidden">{{{description}}}</div>
                      {{#if description}}
                        <a class="read-more inline-flex items-center px-3 py-2 text-sm font-medium text-center text-white bg-blue-700 rounded-lg hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">
                            Read more
                            <svg class="rtl:rotate-180 w-3.5 h-3.5 ms-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
                                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 5h12m0 0L9 1m4 4L9 9"/>
                            </svg>
                        </a>
                      {{/if}}
                      {{#each buttons}}
                      <button class="card-button list-btn uppercase pt-2 pb-1 px-4 flex-grow hover:bg-gradient-to-t md:flex-none" data-index="{{@index}}" data-url="{{this.url}}" data-type="{{this.type}}">{{this.label}}</button>
                      {{/each}}
                  </div>
                </div>
              {{/each}}
            </div>

        <div class="absolute z-30 flex overflow-x-auto -translate-x-1/2 space-x-1 rtl:space-x-reverse bottom-5 left-1/2 scrollbar-visible">
        {{#if (lte items.length 5)}}  
          {{#each items}}
              <button
                  id="carousel-indicator-{{@index}}"
                  type="button"
                  class="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 rounded-full"
                  aria-current="true"
                  aria-label="Slide {{@index}}"
              ></button>
          {{/each}}
        {{/if}}
      </div>

  <!-- Controls -->
  <button
    type="button"
    class="absolute top-0 left-0 z-30 flex items-center justify-center h-full px-4 cursor-pointer group focus:outline-none"
    id="data-carousel-prev"
    data-carousel-prev
  >
    <span
      class="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-600"
    >
      <svg
        aria-hidden="true"
        class="w-6 h-6 text-white"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 19l-7-7 7-7"
        ></path>
      </svg>
      <span class="sr-only">Previous</span>
    </span>
  </button>
  <button
    type="button"
    class="absolute top-0 right-0 z-30 flex items-center justify-center h-full px-4 cursor-pointer group focus:outline-none"
    id="data-carousel-next"
    data-carousel-next
  >
    <span
      class="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-600"
    >
      <svg
        aria-hidden="true"
        class="w-6 h-6 text-white dark:text-gray-800"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 5l7 7-7 7"
        ></path>
      </svg>
      <span class="sr-only">Next</span>
    </span>
  </button>
</div>`,
        },
        {
          name: "detail",
          content: `
          <div class="flex flex-col items-center justify-center w-full h-full p-4">
          <div class="w-full md:col-span-2 h-fit">
            <dynamic-form form="{{json form}}" formSettings="{{json formSettings}}" theme="{{theme}}"/>
          </div>   
          <div>    
            {{#each buttons}}    
              <button class="panel-button list-btn text-white" data-id="{{this.index}}" data-type="{{this.type}}">{{this.label}}</button>     
            {{/each}}
          </div>
          </div>`,
        },
      ]
      return sliderArray
  }
}

export function splitContent(content) {
  // Find the first occurrence of the opening and closing brackets
  const startBracket = content.indexOf("[")
  const endBracket = content.indexOf("]", startBracket)

  // Ensure both brackets exist in the string
  if (startBracket !== -1 && endBracket !== -1) {
    // Extract the parts based on the brackets' positions
    const before = content.substring(0, startBracket) // Before the brackets
    const inside = content.substring(startBracket + 1, endBracket) // Inside the brackets
    let after = content.substring(endBracket + 1) // After the brackets
    after = after.replace("<p></p>", "") // Remove empty paragraph tags
    return { before, inside, after }
  } else {
    console.log("No brackets found in the HTML string.")
    return { before: content, inside: null, after: null }
  }
}

export function getBroswerLang() {
  const browserLang = navigator.language || navigator["userLanguage"]
  const normalizedLang = browserLang.split("-")[0] // e.g., "en-US" → "en"
  return normalizedLang
}

export function getAllLanguagesInSurvey(surveyJson: any): Set<string> {
  const langs = new Set<string>()

  function isMultiLangObject(obj: any): boolean {
    return typeof obj === "object" && obj !== null && Object.keys(obj).length > 0 && Object.values(obj).every(value => typeof value === "string")
  }

  function extractLangs(obj: any) {
    if (Array.isArray(obj)) {
      obj.forEach(item => extractLangs(item))
    } else if (typeof obj === "object" && obj !== null) {
      if (isMultiLangObject(obj) && Object.keys(obj).includes("default")) {
        Object.keys(obj).forEach(key => langs.add(key))
      }
      for (const key in obj) {
        extractLangs(obj[key])
      }
    }
  }

  extractLangs(surveyJson)
  return langs
}

export function isString(val: unknown): val is string {
  return typeof val === "string"
}

export function isObject(value: unknown) {
  return typeof value === "object" && value !== null
}

export function customOptions(context, options) {
  var defaults = context && context.options ? context.options : {}
  return Object.assign({}, defaults, options.hash)
}

export function chop(str: unknown): string {
  if (!isString(str)) return ""
  var re = /^[-_.\W\s]+|[-_.\W\s]+$/g
  // console.log("chop", str, re)
  return str.trim().replace(re, "")
}

export function changecase(str: string, fn: (ch: string) => string): string {
  if (!isString(str)) return ""
  if (str.length === 1) {
    return str.toLowerCase()
  }

  str = chop(str).toLowerCase()
  if (typeof fn !== "function") {
    fn = function identity<T>(x: T): T {
      return x
    }
  }

  var re = /[-_.\W\s]+(\w|$)/g
  return str.replace(re, function (_, ch) {
    return fn(ch)
  })
}

export function options(thisArg, locals, options) {
  if (isOptions(thisArg)) {
    return options({}, locals, thisArg)
  }
  if (isOptions(locals)) {
    return options(thisArg, options, locals)
  }
  options = options || {}
  if (!isOptions(options)) {
    locals = Object.assign({}, locals, options)
  }
  var opts = Object.assign({}, locals, options.hash)
  if (isObject(thisArg)) {
    opts = Object.assign({}, thisArg.options, opts)
  }
  if (opts[options.name]) {
    opts = Object.assign({}, opts[options.name], opts)
  }
  return opts
}

export function isOptions(val) {
  return isObject(val) && isObject(val.hash)
}

export function isUndefined(val) {
  return val == null || (isOptions(val) && val.hash != null)
}

export function indexOf(array, value) {
  for (var i = 0, len = array.length; i < len; i++) {
    if (array[i] === value) {
      return i
    }
  }
  return -1
}

export function utilValue(val, context, options) {
  if (isOptions(val)) {
    return utilValue(null, val, options)
  }
  if (isOptions(context)) {
    return utilValue(val, {}, context)
  }
  if (isBlock(options)) {
    return !!val ? options.fn(context) : options.inverse(context)
  }
  return val
}

export function isBlock(options) {
  return isOptions(options) && typeof options.fn === "function" && typeof options.inverse === "function"
}

export function utilResult(val) {
  if (typeof val === "function") {
    return val.apply(this, [].slice.call(arguments, 1))
  }
  return val
}
